/**
 * How a lead is identified in the UI.
 *
 * Each handle lives in exactly one slot: a number in `phone`, Meta's opaque id
 * (`CO.1734963930960797`) in `whatsappUserId`. This file used to carry a copy of
 * the backend's BSUID regex so it could pull the id back out of the phone slot on
 * a legacy row; the writer change and its migration removed both the source and
 * the rows, so the copy is gone and there is nothing left to keep in sync.
 *
 * A phoneless lead is now simply one with an empty `phone`.
 */

// Colombian mobile grouping, kept from the inbox profile panel so no formatting is
// lost. Anything that doesn't match is shown exactly as stored — a malformed number
// from an ERP import is still the merchant's data, so we never hide or reshape it.
const formatPhoneDisplay = (phone: string): string => {
  const cleaned = phone.replace(/[\s+]/g, "");
  if (/^57\d{10}$/.test(cleaned)) {
    return `+57 ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
  }
  return phone;
};

export type ContactDisplay =
  | { kind: "none" }
  | {
      kind: "phone" | "username" | "bsuid";
      /** Field label above the value. */
      label: string;
      /** Shorter form for toasts, e.g. `${shortLabel} copiado`. */
      shortLabel: string;
      /** What to render. */
      value: string;
      /** What to put on the clipboard — the bare handle, not the `@` form. */
      copyValue: string;
    };

/** The three handle slots every lead-shaped row carries. */
export type ContactInput = {
  phone?: string | null;
  whatsappUsername?: string | null;
  whatsappUserId?: string | null;
};

/**
 * The lead's contact slot. Only a phoneless lead diverts to the username — a
 * malformed but real number still shows under "Teléfono", matching what the
 * reports export does. Mirrors resolveLeadContact in
 * packages/agent/src/helpers/leadContact.ts; the duplication is deliberate,
 * because @agent/* is type-only at runtime.
 */
export const contactDisplay = (lead: ContactInput): ContactDisplay => {
  const phone = lead.phone?.trim() ?? "";
  const username = lead.whatsappUsername?.trim() ?? "";

  if (!phone) {
    if (username)
      return {
        kind: "username",
        label: "Usuario de WhatsApp",
        shortLabel: "Usuario",
        value: `@${username}`,
        copyValue: username,
      };

    // No username: the id is the only handle left, and showing it keeps the row
    // reconcilable against Meta. Mirrors the "ID de WhatsApp" column the reports
    // export has always carried (helpers/reports.ts contactColumns).
    const bsuid = lead.whatsappUserId?.trim() ?? "";
    if (bsuid)
      return {
        kind: "bsuid",
        label: "ID de WhatsApp",
        shortLabel: "ID",
        value: bsuid,
        copyValue: bsuid,
      };

    return { kind: "none" };
  }

  return {
    kind: "phone",
    label: "Teléfono",
    shortLabel: "Teléfono",
    value: formatPhoneDisplay(phone),
    copyValue: phone,
  };
};

/**
 * Title for a lead: its name, else its `@handle`, else its number, else an
 * explicit placeholder. Never the WhatsApp id. Replaces the
 * `lead.name?.trim() || lead.phone` idiom, which rendered
 * `CO.1734963930960797` where a person's name belongs.
 *
 * Only pass a `phone` you are willing to see as a title. Callers holding a
 * denormalized copy of the slot rather than the lead's own — the ticket cards
 * and their `customerPhone` — must not, because those copies were never part of
 * the migration that cleaned `Lead.phone`.
 */
export const leadDisplayName = (
  lead: ContactInput & { name?: string | null }
): string => {
  const name = lead.name?.trim() ?? "";
  const username = lead.whatsappUsername?.trim() ?? "";

  // Meta sends username-only contacts with no display name, and the inbound path
  // stores the handle in `name` (consumers.ts `name ?? username`). Showing it bare
  // reads as an odd name; as `@handle` it reads as what it is.
  if (name && name === username) return `@${username}`;
  if (name) return name;
  if (username) return `@${username}`;

  const phone = lead.phone?.trim() ?? "";
  return phone || "Sin nombre";
};
